import { type FC, useCallback } from 'react';
import { BsPlus } from 'react-icons/bs';
import { Button, Form } from 'react-bootstrap';
import { DragDropContext } from 'react-beautiful-dnd';
import DelayedDroppableWrapper from '@editor2/components/DelayedDroppableWrapper';
import * as uuid from 'uuid';

import CodeMirrorHTML from '@editor2/components/CodeMirrorHTML';
import CopyInput from '@editor2/components/CopyInputWrapper';
import { useDebounceProtectedState } from '@editor2/hooks/useDebounceProtectedState';
import {
	type MCChoicePayload,
	type MCQuestion as MCQuestionType,
	ElementType
} from '@editor2/types/pageElements';

import MCChoice from './MCChoice';
import { mcQuestionStyle } from './styles';

interface Props {
	element: MCQuestionType;
	onChange: (_) => void;
	onDebouncedChange: (_) => void;
}

const MCQuestion: FC<Props> = (props) => {
	const { element, onChange, onDebouncedChange } = props;

	// Mapper for hook consistency - simply extracts the field we need to track locally
	const questionMapper = useCallback(({ body }: MCQuestionType) => ({ body }), []);

	const [questionState, handleLocalChange] = useDebounceProtectedState(element, questionMapper);

	const codeMirrorOptions = {
		lineNumbers: false,
		foldGutter: false,
		gutters: []
	};

	const handleQuestionBodyChange = (value: string) => {
		handleLocalChange({ body: value });
		onDebouncedChange({ body: value });
	};

	const handleDragEnd = (result) => {
		const { source, destination, type } = result;
		const { index: sourceIndex } = source;
		const { index: destinationIndex } = destination;

		if (type === 'MCChoice') {
			const choices = [
				...element.choices.slice(0, sourceIndex),
				...element.choices.slice(sourceIndex + 1)
			];

			onChange({
				choices: [
					...choices.slice(0, destinationIndex),
					element.choices[sourceIndex],
					...choices.slice(destinationIndex)
				]
			});
		}
	};

	const handleAddChoiceClick = () => {
		const newChoice: MCChoicePayload = {
			family_id: uuid.v4(),
			is_correct: false,
			body: '',
			rejoinder: '',
			metadata: {},
			type: ElementType.MCChoice
		};

		onChange({ choices: [...element.choices, newChoice] });
	};

	return (
		<Form className={mcQuestionStyle}>
			<CopyInput label="Family ID" value={element.family_id} />

			<Form.Group>
				<CodeMirrorHTML
					value={questionState.body}
					showTips={false}
					options={{ ...codeMirrorOptions, placeholder: 'Question' }}
					onChange={(value) => handleQuestionBodyChange(value)}
				/>
			</Form.Group>

			<DragDropContext onDragEnd={handleDragEnd}>
				<DelayedDroppableWrapper droppableId={element.family_id} type="MCChoice">
					{(provided) => (
						<div ref={provided.innerRef} {...provided.droppableProps} className="choicesContainer">
							{element.choices.map((choice, i) => (
								<MCChoice
									element={element}
									choice={choice}
									index={i}
									key={choice.family_id}
									onChange={onChange}
									onDebouncedChange={onDebouncedChange}
								/>
							))}
							{provided.placeholder}
						</div>
					)}
				</DelayedDroppableWrapper>
			</DragDropContext>

			<Button
				variant="outline-secondary"
				onClick={() => handleAddChoiceClick()}
				className="addChoiceButton">
				<BsPlus />
				Add choice
			</Button>
		</Form>
	);
};

export default MCQuestion;
